import { DialogRef } from '@angular/cdk/dialog'
import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core'
import { NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms'

import { HlmButtonImports } from '@yoshu-lib/ui/button/src'
import { HlmDialogImports } from '@yoshu-lib/ui/dialog/src'
import { HlmFormFieldImports } from '@yoshu-lib/ui/form-field/src'
import { HlmIconImports } from '@yoshu-lib/ui/icon/src'
import { HlmInputImports } from '@yoshu-lib/ui/input/src'
import { HlmLabelImports } from '@yoshu-lib/ui/label/src'

import { ProtocolSession } from '@yoshu-lib/core/api/protocol-sessions/protocol-sessions.model'
import { ProtocolSessionsService } from '@yoshu-lib/core/api/protocol-sessions/protocol-sessions.service'

import { NgIcon } from '@ng-icons/core'
import { TranslatePipe } from '@ngx-translate/core'
import { injectBrnDialogContext } from '@spartan-ng/brain/dialog'

export interface StepFeedbackDialogContext {
  sessionId: string
  /** Step the note is attached to (stamped on the API entry). */
  stepSlug?: string
  /** Hide the dialog chrome X; pair with disableClose on open(). */
  $hideCloseButton?: boolean
}

/**
 * Mid-session free-text note — does not close the protocol session.
 * Distinct from END {@link SessionFeedbackDialogComponent} (no rating / questions).
 */
@Component({
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    HlmDialogImports,
    HlmFormFieldImports,
    HlmInputImports,
    HlmLabelImports,
    HlmButtonImports,
    NgIcon,
    HlmIconImports,
    ReactiveFormsModule,
    TranslatePipe
  ],
  selector: 'app-step-feedback-dialog',
  host: {
    class: 'block w-full min-w-0 max-w-full'
  },
  template: `
    <hlm-dialog-header class="mb-6">
      <h3 hlmDialogTitle>{{ 'step_mode.step_feedback.title' | translate }}</h3>
      <p hlmDialogDescription>{{ 'step_mode.step_feedback.description' | translate }}</p>
    </hlm-dialog-header>

    <form
      class="flex min-h-0 w-full min-w-0 max-w-full flex-col gap-6"
      [formGroup]="form"
      (ngSubmit)="submit()"
    >
      <hlm-form-field class="gap-1.5">
        <label hlmLabel class="flex flex-col items-start gap-2">
          {{ 'step_mode.step_feedback.label' | translate }}
          <textarea
            hlmInput
            formControlName="text"
            class="!mb-0 h-[106px] resize-none p-4"
            [placeholder]="'step_mode.step_feedback.placeholder' | translate"
          ></textarea>
        </label>
      </hlm-form-field>

      <div class="flex justify-end gap-3">
        <button
          hlmBtn
          type="button"
          variant="outline"
          class="w-fit"
          [disabled]="isLoading()"
          (click)="cancel()"
        >
          {{ 'buttons.cancel' | translate }}
        </button>
        <button hlmBtn type="submit" class="w-fit" [disabled]="submitDisabled()">
          @if (isLoading()) {
            <ng-icon hlm name="loader-circle" size="sm" class="animate-spin" />
          }
          {{ 'step_mode.step_feedback.submit' | translate }}
        </button>
      </div>
    </form>
  `
})
export class StepFeedbackDialogComponent {
  private readonly dialogRef = inject(DialogRef)
  private readonly fb = inject(NonNullableFormBuilder)
  private readonly sessionsService = inject(ProtocolSessionsService)

  readonly context = injectBrnDialogContext<StepFeedbackDialogContext>()
  readonly isLoading = signal(false)

  readonly form = this.fb.group({
    text: this.fb.control('')
  })

  submitDisabled() {
    return this.isLoading() || !this.form.controls.text.value.trim()
  }

  /** Dismiss without posting — dialog chrome hides X / backdrop close. */
  cancel() {
    if (this.isLoading()) return
    this.dialogRef.close(null)
  }

  submit() {
    const text = this.form.controls.text.value.trim()
    if (!text || this.isLoading()) return

    this.isLoading.set(true)
    this.sessionsService
      .addFeedbackStep(this.context.sessionId, text, this.context.stepSlug)
      .subscribe({
        next: (session: ProtocolSession) => {
          this.isLoading.set(false)
          this.dialogRef.close(session)
        },
        error: () => {
          this.isLoading.set(false)
        }
      })
  }
}
